import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

function getAnimationNames(userData)
{
    return [
        ...(userData.OnSelectAnimations || []),
        ...(userData.OnPointerEnterAnimations || []),
        ...(userData.OnPointerExitAnimations || []),
        ...(userData.LoopingAnimations || []),
    ]
}

/**
 * InteractableItem component for non-physics objects.
 * Uses a clean clone that syncs with the original object's world transform.
 */
function InteractableItem({ obj, callbacks, actions })
{
    // Reuse vectors to avoid allocation
    const meshRef = useRef()

    // Clone the object and reset its local transforms to identity
    const clonedObj = useMemo(() =>
    {
        const clone = obj.clone()
        clone.position.set(0, 0, 0)
        clone.rotation.set(0, 0, 0)
        clone.scale.set(1, 1, 1)

        // Make the clone invisible but interactable (hitbox)
        // ESSENTIAL: The clone does NOT receive morph/skeletal animations from the main mixer.
        // It must be invisible so the underlying (correctly animated) original object is seen.
        // We use colorWrite: false so it doesn't draw anything but still hits raycasts.
        const invisibleMat = new THREE.MeshBasicMaterial({
            colorWrite: false,
            depthWrite: false,
            visible: true // Must be true for raycaster to hit it
        })

        clone.traverse((child) =>
        {
            if (child.isMesh)
            {
                // Force all meshes (even multi-material ones) to use the single invisible hitbox material
                child.material = invisibleMat
            }
        })

        return clone
    }, [ obj ])

    // Sync with original object's world transform
    // We need to properly calculate the relative transform because this object is mounted in a new React parent,
    // which might itself have a transform (e.g. be part of a scaled Group).
    const tempMatrix = useMemo(() => new THREE.Matrix4(), [])

    useFrame(() =>
    {
        if (!meshRef.current || !meshRef.current.parent) return

        // Ensure the source object's world matrix is up to date
        // This is critical if the object is being moved by an animation or scroll logic
        // that runs outside the standard scene graph update or before/after this frame.
        obj.updateWorldMatrix(true, false)

        // 1. Get the parent's world matrix and invert it to get "World-to-Local" matrix for this container
        const parent = meshRef.current.parent

        // 2. Calculate the target matrix in the parent's local space
        // LocalMatrix = ParentWorldInverse * ObjectWorld
        tempMatrix.copy(parent.matrixWorld).invert().multiply(obj.matrixWorld)

        // 3. Decompose into the refs local components
        tempMatrix.decompose(meshRef.current.position, meshRef.current.quaternion, meshRef.current.scale)
    })

    // Use SceneManager's calculated world transforms for initial state
    const initialPos = obj.userData?.worldPosition || obj.position
    const initialRot = obj.userData?.worldRotation || obj.quaternion
    const initialScale = obj.userData?.worldScale || new THREE.Vector3(1, 1, 1)

    return (
        <group
            ref={meshRef}
            position={initialPos}
            quaternion={initialRot}
            scale={initialScale}
            {...callbacks}
        >
            <primitive
                object={clonedObj}
                visible={true}
            />
        </group>
    )
}

/**
 * InteractableObjects manager for non-physics objects.
 */
export function InteractableObjects({ sceneManager, interactionManager })
{
    const [ interactables, setInteractables ] = useState([])

    /**
     * Get animation actions bound to an object.
     */
    const getActions = useCallback((obj) =>
    {
        const boundActions = sceneManager.getBoundedActions(obj)
        const animationNames = getAnimationNames(obj.userData)

        if (boundActions.length > 0 || animationNames.length > 0)
        {
            const actions = [ ...boundActions ]

            animationNames.forEach(name =>
            {
                const action = sceneManager.getAnimationAction(name)
                if (action)
                {
                    actions.push(action)
                }
            })

            return actions
        }

        return null
    }, [ sceneManager ])

    /**
     * Create event callbacks for object.
     */
    const getCallbacks = useCallback((obj) =>
    {
        const callbacks = {}
        const userData = obj.userData || {}

        const hasSelectTrigger = userData.OnSelectAnimations ||
            userData.mediaTrigger === 'OnSelect' ||
            userData.type === 'interactable'

        const hasEnterTrigger = userData.OnPointerEnterAnimations ||
            userData.mediaTrigger === 'OnPointerEnter' ||
            userData.type === 'interactable'

        const hasExitTrigger = userData.OnPointerExitAnimations ||
            userData.mediaTrigger === 'OnPointerExit' ||
            userData.type === 'interactable'

        if (hasSelectTrigger)
        {
            callbacks.onClick = (event) =>
            {
                event.object = obj
                interactionManager?.handleInteraction(event)
            }
        }

        if (hasEnterTrigger)
        {
            callbacks.onPointerEnter = (event) =>
            {
                event.object = obj
                interactionManager?.handlePointerEnter(event)
            }
        }

        if (hasExitTrigger)
        {
            callbacks.onPointerLeave = (event) =>
            {
                event.object = obj
                interactionManager?.handlePointerExit(event)
            }
        }

        return callbacks
    }, [ interactionManager ])

    useEffect(() =>
    {
        if (!sceneManager) return

        const objects = sceneManager.getInteractables() || []

        // Process objects to ensure visibility is handled
        // We no longer force hide the original object.
        // Instead, the InteractableItem creates an invisible "Hitbox" clone that tracks the original.
        // This preserves animations and visual fidelity of the original object in the main scene.
        const processed = objects.map(obj =>
        {
            return {
                obj,
                actions: getActions(obj),
                callbacks: getCallbacks(obj)
            }
        })

        setInteractables(processed)
    }, [ sceneManager, getActions, getCallbacks ])

    if (interactables.length === 0) return null

    return (
        <>
            {interactables.map((item, index) => (
                <InteractableItem
                    key={`interactable-${item.obj.name || index}`}
                    obj={item.obj}
                    actions={item.actions}
                    callbacks={item.callbacks}
                />
            ))}
        </>
    )
}

export default InteractableObjects
